import { showNotification } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  BulkEditReposItem,
  BulkEditReposRequest,
  BulkEditReposResponse,
} from "@protos/projects/v1/projects";
import { authDelete, authPatch, authPut } from "@shared/api";
import { DEPLOYMENT_URL } from "@shared/constants";
import {
  ApiError,
  Repo,
  RepoChanges,
  SubmitDeploymentRepoResponse,
} from "@shared/types";

import { useOrg } from "../useOrg";
import { useOrgAssertDefined } from "../useOrgAssertDefined";

export const bulkUpdateDeploymentRepos = async (
  deploymentId: number,
  changes: BulkEditReposItem[]
) => {
  const body: BulkEditReposRequest = {
    deploymentId: deploymentId,
    changes,
  };
  return authPatch<BulkEditReposRequest, BulkEditReposResponse>(
    `${DEPLOYMENT_URL}/${deploymentId}/repos`,
    body
  ).then((response) => BulkEditReposResponse.fromJSON(response));
};

const submitDeploymentRepo = (
  deploymentId: number,
  repoName: string,
  repoChanges: RepoChanges
) => {
  return authPut<RepoChanges, SubmitDeploymentRepoResponse>(
    `${DEPLOYMENT_URL}/${deploymentId}/repos/${repoName}`,
    repoChanges
  );
};

export const deleteDeploymentRepo = (
  deploymentId: number,
  repoId: number
): Promise<string> => {
  const path = `${DEPLOYMENT_URL}/${deploymentId}/repos/${repoId}`;
  return authDelete<string>(path);
};

type UpdateRepoParams = {
  deploymentId: number;
  repoName: string;
  repoChanges: RepoChanges;
};

export const useUpdateRepoMutation = () => {
  const queryClient = useQueryClient();
  const [org] = useOrg();

  return useMutation({
    mutationFn: ({ deploymentId, repoName, repoChanges }: UpdateRepoParams) => {
      return submitDeploymentRepo(deploymentId, repoName, repoChanges);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["repos", { org }],
      });
      // To auto-update the onboarding checklist when repository is updated
      queryClient.invalidateQueries({
        queryKey: ["checklistProgress"],
      });
      // for paginated repos
      queryClient.invalidateQueries({
        queryKey: ["repos", org?.id],
      });
      data.name &&
        queryClient.invalidateQueries({
          queryKey: ["repo", { org }, data.name],
        });
      queryClient.invalidateQueries({
        queryKey: ["DeploymentFindingFilters"],
      });
      queryClient.invalidateQueries({
        queryKey: ["finding"],
      });
    },
  });
};

export const useDeleteRepoMutation = () => {
  const queryClient = useQueryClient();
  const org = useOrgAssertDefined();

  return useMutation({
    mutationFn: (repo: Repo) => {
      return deleteDeploymentRepo(org.id, repo.id);
    },
    onSuccess: (data, repo) => {
      queryClient.invalidateQueries({
        queryKey: ["repos", { org }],
      });
      // for paginated repos
      queryClient.invalidateQueries({
        queryKey: ["repos", org.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["repo", { org }, data],
      });
      showNotification({
        message: `Removed project '${repo.name}' from Semgrep`,
        color: "green",
      });
    },
    onError: (error: ApiError, repo) => {
      showNotification({
        message: `Error deleting '${repo.name}': ${error.message}`,
        color: "red",
      });
    },
  });
};

type BulkUpdateRepoParams = {
  deploymentId: number;
  repoChanges: BulkEditReposItem[];
};

export const useBulkUpdateRepoMutation = () => {
  const queryClient = useQueryClient();
  const [org] = useOrg();

  return useMutation({
    mutationFn: ({ deploymentId, repoChanges }: BulkUpdateRepoParams) => {
      return bulkUpdateDeploymentRepos(deploymentId, repoChanges);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["repos", { org }],
      });
      // for paginated repos
      queryClient.invalidateQueries({
        queryKey: ["repos", org?.id],
      });
      data.updatedRepoNames.forEach((newRepo) => {
        queryClient.invalidateQueries({
          queryKey: ["repo", { org }, newRepo],
        });
      });
    },
  });
};
